import { compose, head, isEmpty, map, pipe, prop, toPairs } from 'ramda'

import { getSearchingAsPolicyAddress } from 'utils/care'

/**
 * These two complementary functions are useful for taking the searchFilters and
 * serializing them into something that we can include in the URL so that
 * filters can be shared and loaded without having to have been manually
 * selected.
 *
 * @param {object} filters The search filters object
 */
export const serializeSearchFilters = (filters = {}) => {
  return compose(btoa, encodeURIComponent, JSON.stringify)(filters)
}
export const deserializeSearchFilters = (filters = btoa('{}')) => {
  // if filters aren't atob-able then just return an empty object
  try {
    return compose(JSON.parse, decodeURIComponent, atob)(filters)
  } catch {
    return {}
  }
}

/** Parse array into options */
export const arrayToOptions = map(data => ({
  name: data,
  label: data,
  value: data,
}))

/** This will get the `fieldName` and parse `key-value pairs` into `options` */
export const getOptionsFrom = fieldName =>
  pipe(
    prop(fieldName),
    toPairs,
    map(([value, label]) => ({
      value,
      label,
      name: label,
    })),
  )

export const getFirstOptionFrom = fieldName => pipe(prop(fieldName), head)

/**
 * GetPolicyInfo has two purposes:
 *
 * 1. It gets the policy address from a policy. Usually the primaryMember has one
 *    but not always. Regardless there can only be one policy address per
 *    account, so we just need to find one to use as our default address for
 *    maps etc.
 * 2. It gets the Uuid of a member that DOES have a policy to set as "searchingAs"
 *    in SearchFilters. When we make a call to the Details endpoint we include a
 *    member uuid and if the member doesn't have a policy the page will error
 *    out.
 */
export const getPolicyInfo = (Member, SearchFilters) => {
  const members = Object.values(Member.members?.entities || {})

  // If there's only one member on the account 'searchingAs' won't
  // exist in the store, so we need to create a similar object
  // to use as a fallback from the first/only member
  let firstMemberUuid = pipe(head, prop('uuid'))(members)
  const firstMember = {
    name: firstMemberUuid,
    value: firstMemberUuid,
  }

  // searchingAs is currently stored in two places so we're checking both
  const filtersSearchingAs = SearchFilters?.filters?.searchingAs
  const activeFiltersSearchingAs = SearchFilters?.activeFilters?.searchingAs
  const searchingAs =
    (!isEmpty(filtersSearchingAs) && filtersSearchingAs) ||
    (!isEmpty(activeFiltersSearchingAs) && activeFiltersSearchingAs) ||
    firstMember ||
    {}

  const policyAddress = getSearchingAsPolicyAddress(members, searchingAs.value)

  // we need an "address" field, which should just be the zip
  if (policyAddress.zip) policyAddress.address = policyAddress.zip

  return {
    policyAddress,
    searchingAs,
  }
}
